import { MdOutlinePersonOff } from "react-icons/md";
import { FILE_STORAGE_SERVER_URL } from "src/helpers/constants";
import { memo, useMemo, useState } from "react";
import { formatLongDate, showNotification } from "src/helpers/utils";
import useUsers from "src/hooks/useUsers";
import NetworkProfile from "../NetworkProfile/NetworkProfile";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import { useTranslation } from "react-i18next";
import useInvites from "src/hooks/useInvites";
import { EPrivacyVisibility } from "../CurrentUserPrivacyModal/CurrentUserPrivacyModal";
import ContactCard, { EContactCardType } from "./ContactCard";
import { ICON_SIZES } from "src/constants/icons";
import { EButtonKind } from "../Button/Button.types";

interface ISentContactInviteCard {
  id: string;
  user: string;
  index: number;
  date: string;
}

const SentContactInviteCard: React.FC<ISentContactInviteCard> = ({
  user,
  index,
  date,
  id,
}) => {
  const { t } = useTranslation();
  const { getUserById } = useUsers();
  const { deleteContactInvite, resetInvitesCache } = useInvites();
  const [showUserInfoModal, setShowUserInfoModal] = useState<boolean>(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState<boolean>(false);
  const [isCancelling, setIsCancelling] = useState<boolean>(false);

  const userInfo = useMemo(() => {
    return getUserById({ _id: user });
  }, [user]);

  const userImage = useMemo(() => {
    if (
      userInfo?.picture &&
      userInfo?.settings?.privacy?.seePicture === EPrivacyVisibility.EVERYONE
    ) {
      return `${FILE_STORAGE_SERVER_URL}/profile-thumbnail-${userInfo?.picture}`;
    }

    return null;
  }, [userInfo]);

  return (
    <>
      <ContactCard
        type={EContactCardType.SENT}
        actions={[
          {
            onClick: () => setShowCancelConfirm(true),
            icon: <MdOutlinePersonOff size={ICON_SIZES.MD} />,
            kind: EButtonKind.DANGER,
            tooltip: t("common:buttons.cancel"),
            isLoading: isCancelling,
          },
        ]}
        index={index}
        subText={formatLongDate(date, true)}
        name={userInfo?.name}
        picture={userImage}
        onClick={() => setShowUserInfoModal(true)}
      />

      {showUserInfoModal && (
        <NetworkProfile
          profileInfo={userInfo}
          onClose={() => setShowUserInfoModal(false)}
          actions={[]}
        />
      )}

      {showCancelConfirm && (
        <ConfirmDialog
          description="¿Deseas cancelar esta invitación de contacto?"
          title="Cancelar"
          onClose={() => setShowCancelConfirm(false)}
          onConfirm={async () => {
            setIsCancelling(true);
            const res = await deleteContactInvite(id);

            if (res?.data?.success) {
              showNotification({
                kind: "success",
                message: "Invitación cancelada",
              });
              await resetInvitesCache();
              setIsCancelling(false);
              setShowCancelConfirm(false);
              setShowUserInfoModal(false);
            } else {
              setIsCancelling(false);
              showNotification({
                kind: "error",
                message: "Error cancelando la invitación",
              });
            }
          }}
          doubleConfirmRequired
          confirmKind={EButtonKind.DANGER}
          isLoading={isCancelling}
        />
      )}
    </>
  );
};

export default memo(SentContactInviteCard);
